/**
 * KiRagService — read-only calls to the KI RAG API for a cited video's
 * subtitles, the same calls the KI RAG web app makes (list, then detail).
 *
 * The cockpit never creates or approves translations; it only shows the
 * subtitles that already exist. A failure (401/403/404, network) never
 * affects the video: it only yields the status "unavailable", with the reason
 * in the console.
 */
import keycloak from '../keycloak';
import { getOrganizationFromToken } from '../utils/tokenUtils';
import {
  type Citation,
  type TranslationItem,
  currentSubtitleRequests,
  kiragPrefix,
  safeUrl,
  subtitleArtifactUrl,
  subtitleListState,
} from '../components/cockpit/citations';

/** One subtitle track that can be played: a current request with a signed URL. */
export interface SubtitleTrack {
  translationId: string;
  language: string;
  src: string;
}

/**
 * off: not asked (no base URL, other host, not signed in, no org);
 * ready: tracks to play; none / outdated / no-speech: what the list says;
 * unavailable: KI RAG could not be read.
 */
export type SubtitleStatus = 'off' | 'ready' | 'none' | 'outdated' | 'no-speech' | 'unavailable';

export interface CitationSubtitles {
  tracks: SubtitleTrack[];
  status: SubtitleStatus;
}

/** KI RAG API base URL (VITE_KIRAG_API_URL); empty disables subtitles. */
export function kiragBaseUrl(): string {
  return (import.meta.env.VITE_KIRAG_API_URL || '').replace(/\/+$/, '');
}

function originOf(url?: string): string | undefined {
  const safe = safeUrl(url);
  return safe ? new URL(safe).origin : undefined;
}

/** True when the URL is served by the configured KI RAG host (false without one). */
export function isKiragUrl(url?: string): boolean {
  const base = originOf(kiragBaseUrl());
  return Boolean(base) && originOf(url) === base;
}

/** Where to read the subtitles of a citation, or undefined when it is not ours to ask. */
interface SubtitleSource {
  base: string;
  prefix: string;
  documentId: string;
  org?: string;
  token: string;
}

async function subtitleSource(c: Citation): Promise<SubtitleSource | undefined> {
  const base = kiragBaseUrl();
  const documentId = c.document?.document_id;
  if (!base || !documentId) return undefined;
  // The bearer token only goes to the configured KI RAG host, and only for a
  // citation whose media is served by that host.
  const mediaOrigin = originOf(c.play_url) ?? originOf(c.document_url);
  if (!mediaOrigin || mediaOrigin !== originOf(base)) return undefined;
  if (!keycloak.authenticated) return undefined;
  try {
    await keycloak.updateToken(30);
  } catch {
    return undefined;
  }
  const token = keycloak.token;
  if (!token) return undefined;
  const org = getOrganizationFromToken(token)?.alias;
  const prefix = kiragPrefix(c, org);
  if (!prefix) return undefined;
  return { base, prefix, documentId, org, token };
}

async function kiragGet<T>(src: SubtitleSource, path: string, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { Authorization: `Bearer ${src.token}` };
  if (src.org) headers['X-Organization-ID'] = src.org;
  const res = await fetch(`${src.base}${src.prefix}/documents/${encodeURIComponent(src.documentId)}${path}`, {
    headers,
    signal,
  });
  if (!res.ok) throw new Error(`KI RAG ${res.status}`);
  return (await res.json()) as T;
}

/** The signed URL of one translation's WebVTT, only when it is on the KI RAG host. */
async function trackUrl(src: SubtitleSource, translationId: string, signal?: AbortSignal): Promise<string | undefined> {
  const d = await kiragGet<{ artifacts?: unknown }>(
    src,
    `/translations/${encodeURIComponent(translationId)}`,
    signal,
  );
  const url = subtitleArtifactUrl(d?.artifacts);
  return url && originOf(url) === originOf(src.base) ? url : undefined;
}

function warn(documentId: string | undefined, reason: unknown) {
  console.warn(`[cockpit] subtitles unavailable for ${documentId ?? 'document'}:`, reason);
}

/**
 * The subtitle tracks available for a cited video (one per language, current
 * and not outdated, whose detail carries a subtitles artifact) and the status
 * the viewer shows when there is none. Never throws.
 */
export async function loadCitationSubtitles(c: Citation, signal?: AbortSignal): Promise<CitationSubtitles> {
  const documentId = c.document?.document_id;
  try {
    const src = await subtitleSource(c);
    if (!src) return { tracks: [], status: 'off' };
    const list = await kiragGet<{ items?: TranslationItem[]; has_speech?: boolean }>(
      src,
      '/translations?limit=50',
      signal,
    );
    const hasSpeech = list?.has_speech !== false;
    const state = subtitleListState(list?.items, hasSpeech);
    if (state !== 'current') return { tracks: [], status: state };
    const current = currentSubtitleRequests(list?.items, hasSpeech);
    const tracks = await Promise.all(
      current.map(async (r) => {
        try {
          const url = await trackUrl(src, r.translation_id, signal);
          if (!url) warn(documentId, `translation ${r.translation_id} has no subtitles URL on the KI RAG host`);
          return url ? { translationId: r.translation_id, language: r.target_language, src: url } : undefined;
        } catch (e) {
          if (!signal?.aborted) warn(documentId, e);
          return undefined;
        }
      }),
    );
    const ok = tracks.filter((t): t is SubtitleTrack => t !== undefined);
    return { tracks: ok, status: ok.length > 0 ? 'ready' : 'unavailable' };
  } catch (e) {
    if (!signal?.aborted) warn(documentId, e);
    return { tracks: [], status: 'unavailable' };
  }
}

/** A fresh signed URL for one track (after the 15-minute URL expired), or undefined. */
export async function refreshSubtitleUrl(c: Citation, translationId: string): Promise<string | undefined> {
  try {
    const src = await subtitleSource(c);
    return src ? await trackUrl(src, translationId) : undefined;
  } catch {
    return undefined;
  }
}
